import { lazy } from "react";
import { Navigate } from "react-router-dom";
import Loadable from "../layouts/loader/Loadable.js";
import { getAuthToken, getUserProfile } from "../utils/_hooks/index.js";
import { useSelector } from "react-redux";
import CustomizeShop from "../screens/CustomizeShop/CustomizeShop.jsx";
/***** Layouts *****/
const FullLayout = Loadable(
  lazy(() => import("../layouts/FullLayout/FullLayout.js"))
);
const BlankLayout = Loadable(lazy(() => import("../layouts/BlankLayout.js")));

/***** Pages *****/
const Landing = Loadable(lazy(() => import("../screens/Landing/Landing.jsx")));
const Login = Loadable(
  lazy(() => import("../screens/Authentication/Login/login.jsx"))
);
const Dashboard = Loadable(
  lazy(() => import("../screens/Dashboard/Dashboard.jsx"))
);
const Restaurants = Loadable(
  lazy(() => import("../screens/Restaurants/View/Restaurants.jsx"))
);
const RestaurantCreate = Loadable(
  lazy(() => import("../screens/Restaurants/Create/RestaurantCreate.jsx"))
);
const RestaurantDetails = Loadable(
  lazy(
    () => import("../screens/Restaurants/DetailsView/RestaurantsDetails.jsx")
  )
);
const DeviceConfiguration = Loadable(
  lazy(
    () => import("../screens/Restaurants/DeviceConfiguration/DeviceConfiguration.jsx")
  )
);
const Subscriptions = Loadable(
  lazy(() => import("../screens/Subcriptions/Subscriptions.jsx"))
);
const CommonMenuItems = Loadable(
  lazy(() => import("../screens/CommonMenuItems/CommonMenuItems.jsx"))
);
const Orders = Loadable(lazy(() => import("../screens/Orders/Orders.jsx")));
const Menu = Loadable(lazy(() => import("../screens/Menu/Menu.jsx")));
const Tables = Loadable(lazy(() => import("../screens/Tables/Tables.jsx")));
const Payments = Loadable(
  lazy(() => import("../screens/Payments/Payments.jsx"))
);
const POS = Loadable(lazy(() => import("../screens/POS/POS.jsx")));
const Staffs = Loadable(lazy(() => import("../screens/Staffs/Staffs.jsx")));
const Leads = Loadable(
  lazy(() => import("../screens/Leads/SuperAdminLeads.jsx"))
);
const Reports = Loadable(lazy(() => import("../screens/Reports/Reports.jsx")));
const StaffReports = Loadable(
  lazy(() => import("../screens/Reports/Staff/StaffReports.jsx"))
);
const Settings = Loadable(
  lazy(() => import("../screens/Settings/Settings.jsx"))
);
const Support = Loadable(lazy(() => import("../screens/Support/Support.jsx")));
const Logout = Loadable(lazy(() => import("../screens/Logout/Logout.jsx")));
const PWAInstallLandingPage = Loadable(
  lazy(() => import("../screens/PWA/PWAInstallLandingPage.jsx"))
);

const Error = Loadable(lazy(() => import("../screens/404/404.js")));

/***** Custom Hook to Get Token *****/
const useAuthToken = () => {
  const localToken = getAuthToken();
  const reduxToken = useSelector((state) => state.authToken.token);
  return localToken || reduxToken;
};

const PublicRouteHandler = () => {
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator?.standalone === true);

  if (isStandalone) {
    return <Navigate to="/login" replace />;
  }
  return <Landing />;
};

/***** Routes Component *****/
const ThemeRoutes = () => {
  const authToken = useAuthToken();

  const navigateToDashboard = () => {
    const user = getUserProfile();
    if (user === "superAdmin") {
      return <Navigate to="/superadmin-dashboard" />;
    }
    const role = (user?.roleName || user?.role || "").toLowerCase();
    const isOwner = role.includes("owner") || !user?.role;
    const perms = (user?.permissionNames || []).map((p) => String(p).toLowerCase());

    if (isOwner || perms.some((p) => p.includes("dashboard"))) {
      return <Navigate to="/dashboard" />;
    }
    if (perms.some((p) => p.includes("kitchen") || p.includes("kot"))) {
      return <Navigate to="/orders" />;
    }
    if (perms.some((p) => p.includes("order") && !p.includes("pos"))) {
      return <Navigate to="/orders" />;
    }
    if (perms.some((p) => p.includes("pos"))) {
      return <Navigate to="/pos" />;
    }
    return <Navigate to="/orders" />;
  };

  return [
    {
      path: "/",
      element: authToken ? <FullLayout /> : <BlankLayout />,
      children: authToken
        ? [
            { path: "/", element: navigateToDashboard() },
            { path: "/login", element: navigateToDashboard() },
            { path: "/home", element: navigateToDashboard() },
            {
              path: "/superadmin-login",
              element: <Navigate to="/superadmin-dashboard" />,
            },
            {
              path: "/superadmin",
              element: <Navigate to="/superadmin-dashboard" />,
            },

            //SuperAdmin
            { path: "/superadmin-dashboard", element: <Dashboard /> },
            { path: "/superadmin-restaurants", element: <Restaurants /> },
            {
              path: "/superadmin-restaurants/create",
              element: <RestaurantCreate />,
            },
            {
              path: "/superadmin-restaurants/:id",
              element: <RestaurantDetails />,
            },
            {
              path: "/superadmin-restaurants/:id/device-configuration",
              element: <DeviceConfiguration />,
            },
            { path: "/superadmin-leads", element: <Leads /> },
            { path: "/superadmin-reports", element: <Reports /> },
            { path: "/superadmin-subscriptions", element: <Subscriptions /> },
            { path: "/superadmin-common-menu", element: <CommonMenuItems /> },
            {
              path: "/superadmin-settings",
              element: <Navigate to="/superadmin-dashboard" />,
            },

            //Admin
            { path: "/dashboard", element: <Dashboard /> },
            { path: "/my-restaurant/:id", element: <RestaurantDetails /> },
            {
              path: "/my-restaurant/:id/device-configuration",
              element: <DeviceConfiguration />,
            },
            { path: "/customize-shop", element: <CustomizeShop /> },
            { path: "/pos", element: <POS /> },
            { path: "/orders", element: <Orders /> },
            { path: "/menu", element: <Menu /> },
            { path: "/tables", element: <Tables /> },
            { path: "/payments", element: <Payments /> },
            { path: "/staff", element: <Staffs /> },
            { path: "/reports", element: <Reports /> },
            { path: "/staff-report", element: <StaffReports /> },
            { path: "/settings", element: <Settings /> },
            { path: "/support", element: <Support /> },

            //Common
            { path: "/install", element: <PWAInstallLandingPage /> },
            { path: "/logout", element: <Logout /> },
            { path: "/*", element: <Error /> },
          ]
        : [
            {
              path: "/superadmin",
              element: <Navigate to="/superadmin-login" />,
            },
            { path: "/superadmin-login", element: <Login /> },
            { path: "/login", element: <Login /> },
            { path: "/install", element: <PWAInstallLandingPage /> },
            { path: "/home", element: <PublicRouteHandler /> },
            { path: "", element: <PublicRouteHandler /> },
            { path: "/", element: <PublicRouteHandler /> },
            { path: "/*", element: <PublicRouteHandler /> },
          ],
    },
    {
      path: "/404",
      element: <Error />,
    },
  ];
};

export default ThemeRoutes;
